import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useForm } from 'services/hooks';
import TagManager from 'react-gtm-module';
import querystring from 'utilities/queryString';
import { GeckoModalService, GeckoToastService } from 'ui';
import GeckoRequest from 'services/gecko-request';
import { renderError } from 'services/gecko-error';
import WebSafeError from 'services/websafe-error';

const questionsExceedMaximumLength = (questions) =>
  questions.some((question) => question.length > 255);

const getClosestResponseFromMisses = (misses) => {
  const [firstMiss, ...otherMisses] = misses;
  const firstClosestResponse = firstMiss.closest_response;
  const areAnyClosestResponseDifferent = otherMisses.some(
    (miss) => miss.closest_response !== firstClosestResponse
  );
  if (areAnyClosestResponseDifferent === false) {
    return firstClosestResponse;
  }
  return null;
};

const missesByIdsSelector = (missIds) => (state) => {
  return missIds.map((missId) => state.botsRoute.missesById[missId]);
};

const getMissQuestionFieldKeyById = (missId) => `question|${missId}`;

const getMissQuestionFieldKey = (miss) => getMissQuestionFieldKeyById(miss.id);

const isMissQuestionFilled = (values, missId) => {
  const fieldKey = getMissQuestionFieldKeyById(missId);
  return Boolean(values[fieldKey]);
};

const areAllMissQuestionsFilled = (values, missIds) => {
  return missIds.every((missId) => isMissQuestionFilled(values, missId));
};

const hasResponseBeenChosen = (values) => {
  return Boolean(values.response_id);
};

const validateQuestions = (values) => {
  const errors = {};

  const questions = Object.entries(values);
  questions.map(([key, value]) => {
    if (key !== 'response_id' && value.length > 255) {
      errors[key] = 'Exceeds maximum length';
    }
  });
  return errors;
};

const useResolveModalBase = ({ botId, missIds, onSubmit }) => {
  const misses = useSelector(missesByIdsSelector(missIds));
  const questionInitialValues = misses.reduce((questionInitialValues, miss) => {
    return {
      ...questionInitialValues,
      [getMissQuestionFieldKey(miss)]: miss.utterance,
    };
  }, {});

  const initialResponseId = getClosestResponseFromMisses(misses) || '';

  const {
    values,
    handleChange,
    setFieldValue,
    handleSubmit,
    errors,
    touched,
  } = useForm({
    initialValues: {
      response_id: initialResponseId,
      ...questionInitialValues,
    },
    requiredFields: ['response_id', ...misses.map(getMissQuestionFieldKey)],
    onSubmit: onSubmit,
    validate: validateQuestions,
  });

  const setResponseId = (newId) => setFieldValue('response_id', newId);

  const conversationSnippetClass = values.response_id
    ? 'col-12 col-sm-6'
    : 'col-12';
  const exampleSnippetClass = values.response_id ? 'col-12 col-sm-6' : 'hidden';

  const closeModal = () => new GeckoModalService().close();

  const utterances = misses.map((miss) => miss.utterance);
  const answerLinkText = `/#/bots/${botId}/answers/new${querystring.stringify({
    name: utterances[0],
    samples: utterances,
    misses: missIds,
  })}`;

  const formHasNotBeenTouched = Object.entries(touched).length === 0;

  let initialErrors = {};
  if (formHasNotBeenTouched) {
    initialErrors = validateQuestions(values);
  }

  return {
    values,
    handleQuestionChange: handleChange,
    setResponseId,
    conversationSnippetClass,
    exampleSnippetClass,
    closeModal,
    answerLinkText,
    handleSubmit,
    errors: formHasNotBeenTouched ? initialErrors : errors,
  };
};

export const useBulkResolveModal = ({ botId, missIds }) => {
  const dataLayer = {
    event: 'open_resolve_bulk_unknown_answer_modal',
    botResolveBotId: botId,
    botResolveMissIds: missIds,
  };

  useEffect(() => {
    TagManager.dataLayer({ dataLayer });
  }, [botId, missIds]);

  const resolveMisses = async (formData) => {
    const { response_id } = formData;
    const questions = missIds.map((missId) => formData[`question|${missId}`]);

    try {
      if (questionsExceedMaximumLength(questions)) {
        throw new Error(
          'Question(s) exceeded max character length, please ensure all questions are below 255 characters'
        );
      }
      await GeckoRequest.post(`/bots/${botId}/misses/bulk-resolve`, {
        miss_ids: missIds,
        response_id,
        questions,
      });
      const dataLayer = {
        event: 'train_resolve_bulk_answer',
        botResolveBotId: botId,
        botResolveMissIds: missIds,
        botResolveResponseId: response_id,
      };
      TagManager.dataLayer({ dataLayer });
      new GeckoToastService().success('Misses resolved');
      new GeckoModalService().close();
    } catch (error) {
      renderError(error);
    }
  };

  const dismissMisses = async () => {
    try {
      await GeckoRequest.post(`/bots/${botId}/misses/bulk-ignore`, {
        miss_ids: missIds,
      });
      const dataLayer = {
        event: 'train_dismiss_bulk_answer',
        botResolveBotId: botId,
        botResolveMissIds: missIds,
      };
      TagManager.dataLayer({ dataLayer });
      new GeckoToastService().success('Misses have been ignored');
      new GeckoModalService().close();
    } catch (error) {
      renderError(error);
    }
  };

  const {
    values,
    handleQuestionChange,
    setResponseId,
    conversationSnippetClass,
    exampleSnippetClass,
    closeModal,
    answerLinkText,
    handleSubmit,
    errors,
  } = useResolveModalBase({
    botId,
    missIds,
    onSubmit: resolveMisses,
  });

  const hasErrors = Object.keys(errors).length;

  const shouldDisableResolveButton = () => {
    return (
      hasErrors ||
      hasResponseBeenChosen(values) === false ||
      areAllMissQuestionsFilled(values, missIds) === false
    );
  };

  const footerBtns = [
    {
      type: 'default',
      name: 'Ignore Misses',
      icon: 'far fa-times',
      handler: dismissMisses,
    },
    {
      type: 'primary',
      name: 'Resolve Answers',
      icon: 'far fa-check',
      disabledWhen: shouldDisableResolveButton,
      handler: handleSubmit,
    },
  ];

  return {
    values,
    handleQuestionChange,
    setResponseId,
    footerBtns,
    conversationSnippetClass,
    exampleSnippetClass,
    closeModal,
    answerLinkText,
  };
};

export const useSingleResolveModal = ({ botId, miss_id }) => {
  const dataLayer = {
    event: 'open_resolve_unknown_answer_modal',
    botResolveBotId: botId,
    botResolveMissId: miss_id,
  };

  useEffect(() => {
    TagManager.dataLayer({ dataLayer });
  }, [botId, miss_id]);

  const resolveMiss = async (formData) => {
    const { response_id } = formData;
    const question = formData[`question|${miss_id}`];
    try {
      if (questionsExceedMaximumLength([question])) {
        throw new WebSafeError(
          'Question exceeded max character length, please ensure all questions are 255 characters or below'
        );
      }
      await GeckoRequest.post(`/bots/${botId}/misses/resolve`, {
        miss_id,
        response_id,
        question,
      });
      const dataLayer = {
        event: 'train_resolve_answer',
        botResolveBotId: botId,
        botResolveMissId: miss_id,
        botResolveResponseId: response_id,
      };
      TagManager.dataLayer({ dataLayer });
      new GeckoToastService().success('Miss resolved');
      new GeckoModalService().close();
    } catch (error) {
      renderError(error);
    }
  };

  const dismissMiss = async () => {
    try {
      await GeckoRequest.post(`/bots/${botId}/misses/ignore`, {
        miss_id,
      });
      const dataLayer = {
        event: 'train_dismiss_answer',
        botResolveBotId: botId,
        botResolveMissId: miss_id,
      };
      TagManager.dataLayer({ dataLayer });
      new GeckoToastService().success('Miss has been ignored');
      new GeckoModalService().close();
    } catch (error) {
      renderError(error);
    }
  };

  const {
    values,
    handleQuestionChange,
    setResponseId,
    conversationSnippetClass,
    exampleSnippetClass,
    closeModal,
    answerLinkText,
    handleSubmit,
    errors,
  } = useResolveModalBase({
    botId,
    missIds: [miss_id],
    onSubmit: resolveMiss,
  });

  const hasErrors = Object.keys(errors).length;

  const shouldDisableResolveButton = () => {
    return (
      hasErrors ||
      hasResponseBeenChosen(values) === false ||
      isMissQuestionFilled(values, miss_id) === false
    );
  };

  const footerBtns = [
    {
      type: 'default',
      name: 'Ignore Miss',
      icon: 'far fa-times',
      handler: dismissMiss,
    },
    {
      type: 'primary',
      name: 'Resolve Answer',
      icon: 'far fa-check',
      disabledWhen: shouldDisableResolveButton,
      handler: handleSubmit,
    },
  ];

  return {
    values,
    handleQuestionChange,
    setResponseId,
    footerBtns,
    conversationSnippetClass,
    exampleSnippetClass,
    closeModal,
    answerLinkText,
  };
};
